import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminCreateFeatureTag,
  getAdminGetFeatureTagsQueryKey,
  useAdminGetProducts,
} from '@/api/auth/admin/admin';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/services/logger';

/**
 * Dialog for adding a new feature catalog tag (product + feature code + description).
 */
export function AddFeatureTagDialog() {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [productId, setProductId] = useState('');
  const [featureCode, setFeatureCode] = useState('');
  const [description, setDescription] = useState('');

  const { data: productsData } = useAdminGetProducts();
  const products = productsData?.data?.products || [];

  const reset = () => {
    setProductId('');
    setFeatureCode('');
    setDescription('');
  };

  const { mutate: createFeatureTag, isPending: isCreating } = useAdminCreateFeatureTag({
    mutation: {
      onSuccess: () => {
        toast.success('Feature tag created');
        setIsOpen(false);
        reset();
        queryClient.invalidateQueries({
          queryKey: getAdminGetFeatureTagsQueryKey(),
        });
      },
      onError: (error: any) => {
        logger.error(error, {
          context: 'CreateFeatureTag',
          productId,
          featureCode: featureCode.trim(),
        });
        toast.error(
          'Failed to create feature tag. ' + (error.response?.data?.detail || error.message)
        );
      },
    },
  });

  const handleAdd = () => {
    createFeatureTag({
      data: {
        productId,
        featureCode: featureCode.trim(),
        description: description.trim(),
      },
    });
  };

  const handleClose = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      reset();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Feature Tag
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Feature Tag</DialogTitle>
          <DialogDescription>
            Define an available feature tag. Organizations can then configure a value against it.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="productId">Product</Label>
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger id="productId">
                <SelectValue placeholder="Select a product" />
              </SelectTrigger>
              <SelectContent>
                {products.map((product) => (
                  <SelectItem key={product.productId} value={product.productId}>
                    {product.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">The product this feature belongs to.</p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="featureCode">Feature Code</Label>
            <Input
              id="featureCode"
              type="text"
              value={featureCode}
              onChange={(e) => setFeatureCode(e.target.value)}
              placeholder="sync.allowAllTables"
              maxLength={200}
            />
            <p className="text-sm text-muted-foreground">
              Globally unique code the consuming product keys off (max 200 characters).
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Allow selecting all tables"
              maxLength={1000}
            />
            <p className="text-sm text-muted-foreground">
              Human-readable explanation shown to administrators (max 1000 characters).
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={isCreating || !productId || !featureCode.trim() || !description.trim()}
          >
            {isCreating ? 'Creating...' : 'Create Feature Tag'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
